import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { ArrowUpRight, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Consultation Form
export const ConsultationForm = ({ className = "" }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/leads`, {
        ...formData,
        lead_type: 'consultation'
      });
      
      setSuccess(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
      
      // Open WhatsApp with pre-filled message
      if (response.data.whatsapp_link) {
        window.open(response.data.whatsapp_link, '_blank');
      }
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`p-8 border border-gold/30 bg-card ${className}`}
        data-testid="consultation-form-success"
      >
        <div className="flex flex-col items-center justify-center text-center py-8">
          <CheckCircle className="w-16 h-16 text-gold mb-4" />
          <h3 className="text-2xl font-serif text-white mb-2">Thank You!</h3>
          <p className="text-muted-foreground mb-6">
            Your consultation request has been submitted. We'll contact you shortly.
          </p>
          <Button
            onClick={() => setSuccess(false)}
            variant="outline"
            className="border-white/20 text-white hover:bg-white hover:text-black"
          >
            Submit Another Request
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.form
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={handleSubmit}
      className={`p-8 border border-white/10 bg-card ${className}`}
      data-testid="consultation-form"
    >
      <h3 className="text-2xl font-serif text-white mb-6">Free Consultation</h3>
      
      <div className="space-y-6">
        <div>
          <Label htmlFor="name" className="text-white/70 text-xs uppercase tracking-widest">Full Name</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:border-gold focus:ring-0 text-white placeholder:text-white/30"
            placeholder="Enter your name"
            required
            data-testid="consultation-name-input"
          />
        </div>
        
        <div>
          <Label htmlFor="email" className="text-white/70 text-xs uppercase tracking-widest">Email</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:border-gold focus:ring-0 text-white placeholder:text-white/30"
            placeholder="Enter your email"
            required
            data-testid="consultation-email-input"
          />
        </div>
        
        <div>
          <Label htmlFor="phone" className="text-white/70 text-xs uppercase tracking-widest">Phone</Label>
          <Input
            id="phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:border-gold focus:ring-0 text-white placeholder:text-white/30"
            placeholder="+91 XXXXX XXXXX"
            required
            data-testid="consultation-phone-input"
          />
        </div>
        
        <div>
          <Label htmlFor="message" className="text-white/70 text-xs uppercase tracking-widest">Message</Label>
          <Textarea
            id="message"
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:border-gold focus:ring-0 text-white placeholder:text-white/30 min-h-[100px] resize-none"
            placeholder="Tell us about your project..."
            data-testid="consultation-message-input"
          />
        </div>

        {error && (
          <p className="text-red-500 text-sm">{error}</p>
        )}

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-gold text-black hover:bg-gold-light rounded-none px-8 py-6 text-sm uppercase tracking-widest font-bold"
          data-testid="consultation-submit-btn"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              Request Consultation
              <ArrowUpRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </motion.form>
  );
};

// Society Assessment Form
export const AssessmentForm = ({ className = "" }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    society_name: '',
    society_address: '',
    num_members: '',
    building_age: '',
    current_fsi: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/leads`, {
        ...formData,
        num_members: formData.num_members ? parseInt(formData.num_members) : null,
        lead_type: 'assessment'
      });
      
      setSuccess(true);
      
      // Open WhatsApp with pre-filled message
      if (response.data.whatsapp_link) {
        window.open(response.data.whatsapp_link, '_blank');
      }
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`p-8 border border-gold/30 bg-card ${className}`}
        data-testid="assessment-form-success"
      >
        <div className="flex flex-col items-center justify-center text-center py-8">
          <CheckCircle className="w-16 h-16 text-gold mb-4" />
          <h3 className="text-2xl font-serif text-white mb-2">Assessment Requested!</h3>
          <p className="text-muted-foreground mb-6">
            Our team will review your society details and contact you within 24 hours.
          </p>
          <Button
            onClick={() => setSuccess(false)}
            variant="outline"
            className="border-white/20 text-white hover:bg-white hover:text-black"
          >
            Submit Another Assessment
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.form
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={handleSubmit}
      className={`p-8 border border-white/10 bg-card ${className}`}
      data-testid="assessment-form"
    >
      <h3 className="text-2xl font-serif text-white mb-2">Society Assessment</h3>
      <p className="text-muted-foreground text-sm mb-6">Get a free redevelopment feasibility assessment</p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label className="text-white/70 text-xs uppercase tracking-widest">Contact Person</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:border-gold focus:ring-0 text-white placeholder:text-white/30"
            placeholder="Your name"
            required
            data-testid="assessment-name-input"
          />
        </div>
        
        <div>
          <Label className="text-white/70 text-xs uppercase tracking-widest">Phone</Label>
          <Input
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:border-gold focus:ring-0 text-white placeholder:text-white/30"
            placeholder="+91 XXXXX XXXXX"
            required
            data-testid="assessment-phone-input"
          />
        </div>
        
        <div>
          <Label className="text-white/70 text-xs uppercase tracking-widest">Email</Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:border-gold focus:ring-0 text-white placeholder:text-white/30"
            placeholder="your@email.com"
            required
            data-testid="assessment-email-input"
          />
        </div>
        
        <div>
          <Label className="text-white/70 text-xs uppercase tracking-widest">Society Name</Label>
          <Input
            value={formData.society_name}
            onChange={(e) => setFormData({ ...formData, society_name: e.target.value })}
            className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:border-gold focus:ring-0 text-white placeholder:text-white/30"
            placeholder="Society/Building name"
            required
            data-testid="assessment-society-input"
          />
        </div>
        
        <div className="md:col-span-2">
          <Label className="text-white/70 text-xs uppercase tracking-widest">Society Address</Label>
          <Input
            value={formData.society_address}
            onChange={(e) => setFormData({ ...formData, society_address: e.target.value })}
            className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:border-gold focus:ring-0 text-white placeholder:text-white/30"
            placeholder="Full address"
            required
            data-testid="assessment-address-input"
          />
        </div>
        
        <div>
          <Label className="text-white/70 text-xs uppercase tracking-widest">Number of Members</Label>
          <Input
            type="number"
            value={formData.num_members}
            onChange={(e) => setFormData({ ...formData, num_members: e.target.value })}
            className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:border-gold focus:ring-0 text-white placeholder:text-white/30"
            placeholder="e.g., 24"
            data-testid="assessment-members-input"
          />
        </div>
        
        <div>
          <Label className="text-white/70 text-xs uppercase tracking-widest">Building Age</Label>
          <Input
            value={formData.building_age}
            onChange={(e) => setFormData({ ...formData, building_age: e.target.value })}
            className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:border-gold focus:ring-0 text-white placeholder:text-white/30"
            placeholder="e.g., 30+ years"
            data-testid="assessment-age-input"
          />
        </div>
        
        <div>
          <Label className="text-white/70 text-xs uppercase tracking-widest">Current FSI</Label>
          <Input
            value={formData.current_fsi}
            onChange={(e) => setFormData({ ...formData, current_fsi: e.target.value })}
            className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:border-gold focus:ring-0 text-white placeholder:text-white/30"
            placeholder="e.g., 1.0"
            data-testid="assessment-fsi-input"
          />
        </div>
        
        <div className="md:col-span-2">
          <Label className="text-white/70 text-xs uppercase tracking-widest">Additional Comments</Label>
          <Textarea
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:border-gold focus:ring-0 text-white placeholder:text-white/30 min-h-[80px] resize-none"
            placeholder="Any specific concerns or requirements..."
            data-testid="assessment-message-input"
          />
        </div>

        {error && (
          <p className="text-red-500 text-sm md:col-span-2">{error}</p>
        )}

        <div className="md:col-span-2">
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gold text-black hover:bg-gold-light rounded-none px-8 py-6 text-sm uppercase tracking-widest font-bold"
            data-testid="assessment-submit-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                Request Free Assessment
                <ArrowUpRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </motion.form>
  );
};

// Contact Form
export const ContactForm = ({ className = "" }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/contact`, formData);
      setSuccess(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      
      // Open WhatsApp with pre-filled message
      if (response.data.whatsapp_link) {
        window.open(response.data.whatsapp_link, '_blank');
      }
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`p-8 border border-gold/30 bg-card ${className}`}
        data-testid="contact-form-success"
      >
        <div className="flex flex-col items-center justify-center text-center py-8">
          <CheckCircle className="w-16 h-16 text-gold mb-4" />
          <h3 className="text-2xl font-serif text-white mb-2">Message Sent!</h3>
          <p className="text-muted-foreground mb-6">
            Thank you for reaching out. We'll respond within 24 hours.
          </p>
          <Button
            onClick={() => setSuccess(false)}
            variant="outline"
            className="border-white/20 text-white hover:bg-white hover:text-black"
          >
            Send Another Message
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.form
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={handleSubmit}
      className={`p-8 border border-white/10 bg-card ${className}`}
      data-testid="contact-form"
    >
      <h3 className="text-2xl font-serif text-white mb-6">Send a Message</h3>
      
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label className="text-white/70 text-xs uppercase tracking-widest">Name</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:border-gold focus:ring-0 text-white placeholder:text-white/30"
              placeholder="Your name"
              required
              data-testid="contact-name-input"
            />
          </div>
          
          <div>
            <Label className="text-white/70 text-xs uppercase tracking-widest">Phone</Label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:border-gold focus:ring-0 text-white placeholder:text-white/30"
              placeholder="+91 XXXXX XXXXX"
              required
              data-testid="contact-phone-input"
            />
          </div>
        </div>
        
        <div>
          <Label className="text-white/70 text-xs uppercase tracking-widest">Email</Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:border-gold focus:ring-0 text-white placeholder:text-white/30"
            placeholder="your@email.com"
            required
            data-testid="contact-email-input"
          />
        </div>
        
        <div>
          <Label className="text-white/70 text-xs uppercase tracking-widest">Subject</Label>
          <Input
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:border-gold focus:ring-0 text-white placeholder:text-white/30"
            placeholder="How can we help?"
            required
            data-testid="contact-subject-input"
          />
        </div>
        
        <div>
          <Label className="text-white/70 text-xs uppercase tracking-widest">Message</Label>
          <Textarea
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:border-gold focus:ring-0 text-white placeholder:text-white/30 min-h-[120px] resize-none"
            placeholder="Your message..."
            required
            data-testid="contact-message-input"
          />
        </div>

        {error && (
          <p className="text-red-500 text-sm">{error}</p>
        )}

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-gold text-black hover:bg-gold-light rounded-none px-8 py-6 text-sm uppercase tracking-widest font-bold"
          data-testid="contact-submit-btn"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              Send Message
              <ArrowUpRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </motion.form>
  );
};
